import i18n from '../i18n';
import { setLanguage } from '../redux/slices/languageSlice';
import { setCookie, getCookie, removeCookie } from './cookieUtils';
import { userService } from './api';

// Constants
const LANGUAGE_STORAGE_KEY = 'userLanguage';
const TEMP_LANGUAGE_COOKIE_NAME = 'tempLanguage'; // For tracking temporary language changes
const COOKIE_EXPIRY_DAYS = 365; // 1 year

/**
 * Language service to manage user language preferences
 * Implements a hybrid approach with localStorage, cookies, and database storage
 */
const languageService = {
  /**
   * Changes the application language and persists the preference
   * @param {string} lang - The language code (e.g., 'en', 'es', 'ca')
   * @param {function} dispatch - Redux dispatch function
   * @param {boolean} isAuthenticated - Whether the user is authenticated
   * @param {boolean} savePreference - Whether to save as user preference (default: true)
   * @returns {Promise<string>} The set language code
   */
  changeLanguage: async (lang, dispatch, isAuthenticated = false, savePreference = true) => {
    // Validate language
    if (!['en', 'es', 'ca', 'fr'].includes(lang)) {
      console.warn(`Unsupported language: ${lang}. Defaulting to English.`);
      lang = 'en';
    }

    console.log(`[languageService] Changing language to: ${lang}`, {
      saveAsPreference: savePreference,
      isAuthenticated,
      currentLanguage: i18n.language,
      fromTemporary: languageService.isUsingTemporaryLanguage()
    });

    // Set language in i18next
    await i18n.changeLanguage(lang);
    
    // Update Redux store if dispatch provided
    if (dispatch) {
      dispatch(setLanguage(lang));
    }
    
    if (savePreference) {
      // Save to localStorage for all users (authenticated and guests)
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      console.log(`[languageService] Saved language preference to localStorage: ${lang}`);
      
      // For authenticated users, also save to database
      if (isAuthenticated) {
        try {
          console.log(`[languageService] User is authenticated, saving language preference to database: ${lang}`);
          // Check if we have a token before making the API call
          const token = localStorage.getItem('accessToken');
          if (!token) {
            console.warn('[languageService] No access token found, skipping database update');
          } else {
            console.log('[languageService] Access token found, proceeding with API call to update language preference');
            const result = await userService.updateLanguagePreference(lang);
            console.log('[languageService] Database update result:', result);
          }
        } catch (error) {
          console.error('[languageService] Failed to update language preference in database:', error);
          // Continue anyway since we have the localStorage fallback
        }
      } else {
        console.log('[languageService] User is not authenticated, skipping database update');
      }
      
      // Clear any temporary language setting (still using cookie for temporary)
      if (getCookie(TEMP_LANGUAGE_COOKIE_NAME)) {
        console.log('Clearing temporary language cookie after setting preference');
        removeCookie(TEMP_LANGUAGE_COOKIE_NAME);
      }
    } else {
      // For temporary language changes, store in a separate cookie
      setCookie(TEMP_LANGUAGE_COOKIE_NAME, lang, 1); // Short expiry for temporary
      console.log(`Temporarily switched to language: ${lang} (not saved as preference)`);
    }
    
    return lang;
  },
  
  /**
   * Gets the user's preferred language (not temporary) from localStorage
   * @returns {string} The preferred language code
   */
  getPreferredLanguage: () => {
    // Read from localStorage, fallback to 'en'
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
  },
  
  /**
   * Gets the temporary language if set (uses cookie)
   * @returns {string|null} The temporary language or null if not set
   */
  getTemporaryLanguage: () => {
    return getCookie(TEMP_LANGUAGE_COOKIE_NAME);
  },
  
  /**
   * Checks if current language is temporary
   * @returns {boolean} True if the current language is temporary
   */
  isUsingTemporaryLanguage: () => {
    const tempLang = getCookie(TEMP_LANGUAGE_COOKIE_NAME);
    return !!tempLang && tempLang === i18n.language;
  },
  
  /**
   * Temporarily switches to a language without saving it as the user's preference
   * @param {string} lang - The language code
   * @param {function} dispatch - Redux dispatch function
   * @returns {Promise<string>} The temporarily set language
   */
  temporarilySwitchLanguage: async (lang, dispatch) => {
    console.log(`[languageService] Temporarily switching language to: ${lang}`, {
      currentLanguage: i18n.language,
      preferredLanguage: languageService.getPreferredLanguage(),
      currentTemporary: languageService.getTemporaryLanguage()
    });
    return await languageService.changeLanguage(lang, dispatch, false, false);
  },
  
  /**
   * Restores the user's preferred language
   * @param {function} dispatch - Redux dispatch function
   * @param {boolean} isAuthenticated - Whether the user is authenticated
   * @returns {Promise<string>} The restored language
   */
  restorePreferredLanguage: async (dispatch, isAuthenticated = false) => {
    // Read preferred language from localStorage
    const preferredLang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
    const currentLang = i18n.language;
    // Temporary language still uses cookie
    const tempLang = getCookie(TEMP_LANGUAGE_COOKIE_NAME);
    
    console.log(`Restoring preferred language check:`, {
      preferredLang,
      currentLang,
      tempLang,
      isUsingTemp: languageService.isUsingTemporaryLanguage()
    });
    
    // Only restore if we're using a temporary language or not on the preferred one
    if (tempLang || currentLang !== preferredLang) {
      console.log(`Restoring preferred language: ${preferredLang}`);
      
      // Clean up temporary language cookie
      if (tempLang) {
        console.log('Removing temporary language cookie');
        removeCookie(TEMP_LANGUAGE_COOKIE_NAME);
      }
      
      // Set to preferred language
      return await languageService.changeLanguage(preferredLang, dispatch, isAuthenticated);
    }
    
    return currentLang;
  },
  
  /**
   * Clears any temporary language setting
   */
  clearTemporaryLanguage: () => {
    if (getCookie(TEMP_LANGUAGE_COOKIE_NAME)) {
      console.log('Clearing temporary language cookie');
      removeCookie(TEMP_LANGUAGE_COOKIE_NAME);
      return true;
    }
    return false;
  },
  
  /**
   * Resets any temporary language and ensures we're using the preferred language
   * @param {function} dispatch - Redux dispatch function 
   * @returns {Promise<boolean>} True if a reset was performed
   */
  resetToPreferredLanguage: async (dispatch) => {
    // Temporary language still uses cookie
    const tempLang = getCookie(TEMP_LANGUAGE_COOKIE_NAME);
    // Read preferred language from localStorage
    const preferredLang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
    
    // Clear temporary language cookie
    if (tempLang) {
      console.log('Resetting from temporary language to preferred language');
      removeCookie(TEMP_LANGUAGE_COOKIE_NAME);
      
      // Set language directly without saving preference
      await i18n.changeLanguage(preferredLang);
      if (dispatch) {
        dispatch(setLanguage(preferredLang));
      }
      return true;
    }
    
    return false;
  },
  
  /**
   * Loads the user's preferred language
   * @param {function} dispatch - Redux dispatch function
   * @param {boolean} isAuthenticated - Whether the user is authenticated
   * @returns {Promise<string>} The loaded language code
   */
  loadPreferredLanguage: async (dispatch, isAuthenticated = false) => {
    let language = 'en'; // Default fallback
    
    // First check localStorage (fast, works for everyone)
    const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLang && ['en', 'es', 'ca', 'fr'].includes(storedLang)) {
      language = storedLang;
    }
    
    // For authenticated users, also try to get from server (authoritative)
    if (isAuthenticated) {
      try {
        const preferences = await userService.getUserPreferences();
        if (preferences.languagePreference && ['en', 'es', 'ca', 'fr'].includes(preferences.languagePreference)) {
          const serverLang = preferences.languagePreference;
          
          // Update language if server value is different
          if (serverLang !== language) {
             language = serverLang;
             // Update localStorage if different from server value
             if (serverLang !== storedLang) {
               localStorage.setItem(LANGUAGE_STORAGE_KEY, serverLang);
               console.log(`[languageService] Updated localStorage language from server preference: ${serverLang}`);
             }
          }
        }
      } catch (error) {
        console.error('Failed to fetch language preference from server:', error);
        // Continue with localStorage value as fallback
      }
    }
    
    // Apply the language
    await i18n.changeLanguage(language);
    
    // Update Redux store
    if (dispatch) {
      dispatch(setLanguage(language));
    }
    
    // Clear any temporary language
    languageService.clearTemporaryLanguage();
    
    return language;
  },
  
  /**
   * Gets the current active language
   * @returns {string} The current language code
   */
  getCurrentLanguage: () => {
    return i18n.language;
  },
  
  /**
   * Checks if the given language is supported
   * @param {string} lang - The language code to check
   * @returns {boolean} True if the language is supported
   */
  isLanguageSupported: (lang) => {
    return ['en', 'es', 'ca', 'fr'].includes(lang);
  }
};

export default languageService; 